import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { usePermissionCatalog } from '../api'
import type { RolePermission } from '../types'
import { ModuleIcon } from './ModuleIcon'

type Props = {
  permissions: string[]
  permissionItems?: RolePermission[]
}

export function RolePermissionSummary({ permissions }: Props) {
  const { t } = useTranslation()
  const catalog = usePermissionCatalog().data
  const granted = new Set(permissions)

  const groups = catalog?.groups.length
    ? catalog.groups
    : (catalog?.modules ?? []).map((m) => ({
        key: m.key,
        title: m.title || m.key,
        description: m.description || '',
        sortOrder: m.sortOrder ?? 0,
        permissions: m.resources.flatMap((r) =>
          r.actions.map((action) => {
            const code = r.codes?.[action] ?? `${m.key}.${r.key}.${action}`
            return {
              id: catalog?.idByCode[code] ?? code,
              code,
              title: catalog?.titleByCode[code] || '',
              description: catalog?.descriptionByCode[code] || '',
              module: m.key,
            }
          }),
        ),
      }))

  return (
    <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
      {groups.map((group) => {
        const codes = group.permissions.map((p) => p.code)
        const grantedItems = group.permissions.filter((p) => granted.has(p.code))
        const count = grantedItems.length
        const pct = codes.length ? Math.round((count / codes.length) * 100) : 0
        return (
          <div key={group.key} className="rounded-xl border bg-card p-3">
            <div className="flex items-center gap-2.5">
              <span className={cn('flex size-8 items-center justify-center rounded-lg', count ? 'bg-brand-blue/10 text-brand-blue' : 'bg-muted text-muted-foreground')}>
                <ModuleIcon module={group.key} className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{group.title}</p>
                {group.description ? <p className="truncate text-xs text-muted-foreground">{group.description}</p> : null}
                <p className="text-xs text-muted-foreground">{t('access.summary.grantedOf', { granted: count, total: codes.length })}</p>
              </div>
              <span className="text-xs font-semibold tabular-nums text-muted-foreground">%{pct}</span>
            </div>
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-brand-blue transition-[width]" style={{ width: `${pct}%` }} />
            </div>
        
          </div>
        )
      })}
    </div>
  )
}
